"use strict";

import gsap from 'gsap';
import { onReady } from '../core/ready.js';

const templatePageNavSelector = '.template-page-nav';
const headerSelector = '#pageHeader';
const navHeightCssVar = '--template-page-nav-height';

onReady(() => {
    initTemplatePageNav();
});

function initTemplatePageNav() {
    const navElements = Array.from(document.querySelectorAll(templatePageNavSelector));

    if (!navElements.length) {
        document.documentElement.style.setProperty(navHeightCssVar, '0px');
        return;
    }

    let maxNavHeight = 0;
    navElements.forEach((navEl) => {
        const navHeight = getNavHeight(navEl);
        if (navHeight > maxNavHeight) {
            maxNavHeight = navHeight;
        }
        markNavAnchorTargets(navEl);
        initSingleTemplatePageNav(navEl);
    });

    document.documentElement.style.setProperty(navHeightCssVar, `${Math.round(maxNavHeight)}px`);
}

function resolveNavTrigger(navEl) {
    const triggerSelector = navEl.dataset.navTrigger;
    if (triggerSelector) {
        const triggerEl = document.querySelector(triggerSelector);
        if (triggerEl) return triggerEl;
    }

    return navEl.previousElementSibling;
}

function isTemplateNavMobileViewport() {
    return window.matchMedia('(max-width: 991px)').matches;
}

function getNavHeight(navEl) {
    if (!navEl) return 0;

    const computedDisplay = window.getComputedStyle(navEl).display;
    if (computedDisplay !== 'none') {
        return navEl.getBoundingClientRect().height;
    }

    const previousDisplay = navEl.style.display;
    const previousVisibility = navEl.style.visibility;
    const previousPointerEvents = navEl.style.pointerEvents;

    navEl.style.display = 'block';
    navEl.style.visibility = 'hidden';
    navEl.style.pointerEvents = 'none';

    const measuredHeight = navEl.getBoundingClientRect().height;

    navEl.style.display = previousDisplay;
    navEl.style.visibility = previousVisibility;
    navEl.style.pointerEvents = previousPointerEvents;

    return measuredHeight;
}

function shouldShowTemplateNav(triggerEl, headerHeight = 0) {
    if (!triggerEl) return false;
    const triggerBottom = triggerEl.getBoundingClientRect().bottom;
    return triggerBottom <= (headerHeight + 1);
}

function markNavAnchorTargets(navEl) {
    const navButtons = navEl.querySelectorAll('.btn-anchor[data-anchor^="#"]');
    navButtons.forEach((buttonEl) => {
        const anchorSelector = buttonEl.getAttribute('data-anchor');
        if (!anchorSelector || anchorSelector === '#') return;

        const targetEl = document.querySelector(anchorSelector);
        if (!targetEl) return;

        targetEl.classList.add('template-page-nav-target');
    });
}

function initSingleTemplatePageNav(navEl) {
    const triggerEl = resolveNavTrigger(navEl);
    if (!triggerEl) return;

    const headerEl = document.querySelector(headerSelector);
    let navTween = null;
    let navIsVisible = false;

    const updateNav = () => {
        if (isTemplateNavMobileViewport()) {
            document.documentElement.style.setProperty(navHeightCssVar, '0px');
            if (navTween) {
                navTween.kill();
                navTween = null;
            }
            navEl.style.display = 'none';
            navEl.style.pointerEvents = '';
            gsap.set(navEl, { clearProps: 'opacity,visibility,transform' });
            navIsVisible = false;
            return;
        }

        const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
        document.documentElement.style.setProperty(navHeightCssVar, `${Math.round(getNavHeight(navEl))}px`);
        const shouldBeVisible = shouldShowTemplateNav(triggerEl, headerHeight);
        navEl.style.top = `${headerHeight}px`;

        if (shouldBeVisible === navIsVisible) return;

        if (navTween) {
            navTween.kill();
            navTween = null;
        }

        if (shouldBeVisible) {
            navEl.style.display = 'block';
            navEl.style.pointerEvents = 'none';
            navTween = gsap.fromTo(
                navEl,
                { autoAlpha: 0, y: -10 },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.28,
                    ease: 'power2.out',
                    onComplete: () => {
                        navEl.style.pointerEvents = '';
                        navTween = null;
                    }
                }
            );
            navIsVisible = true;
            return;
        }

        navEl.style.pointerEvents = 'none';
        navTween = gsap.to(navEl, {
            autoAlpha: 0,
            y: -10,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
                navEl.style.display = 'none';
                navEl.style.pointerEvents = '';
                gsap.set(navEl, { clearProps: 'opacity,visibility,transform' });
                navTween = null;
            }
        });
        navIsVisible = false;
    };

    updateNav();
    window.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);
}
